import { Suspense, lazy, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import Lenis from "lenis";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import CookieBanner from "@/components/CookieBanner";
import Mascotte from "@/components/Mascotte";
import NewsletterPopup from "@/components/NewsletterPopup";
import ServerDownBanner from "@/components/ServerDownBanner";
import BussolaLoader from "@/components/BussolaLoader";
import { getCreatorCode, setCreatorCode } from "@/lib/creatorLink";

// Creator loggato: recupera il suo codice e lo attacca automaticamente all'indirizzo,
// così qualsiasi link copi o condivida porta già il suo referral
function CreatorRefSync() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { setCreatorCode(""); return; }
    api.get("/creator/me")
      .then((r) => setCreatorCode(r.data.code))
      .catch(() => setCreatorCode(""));
  }, [user]);

  useEffect(() => {
    const code = getCreatorCode();
    if (!code) return;
    if (location.pathname.startsWith("/admin")) return;
    const params = new URLSearchParams(location.search);
    if (params.get("ref")) return; // non sovrascrivere mai un link creator già aperto
    params.set("ref", code);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return null;
}
import Home from "@/pages/Home";
import Maintenance from "@/pages/Maintenance";
import Catalogo from "@/pages/Catalogo";
// Code splitting per rotta: il chunk iniziale resta leggero (Home/Catalogo),
// il resto — admin, grafici, scanner QR — si carica solo quando serve.
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const Fedelta = lazy(() => import("@/pages/Fedelta"));
const Profilo = lazy(() => import("@/pages/Profilo"));
const Servizi = lazy(() => import("@/pages/Servizi"));
const Eventi = lazy(() => import("@/pages/Eventi"));
const Wanted = lazy(() => import("@/pages/Wanted"));
const WantedDetail = lazy(() => import("@/pages/WantedDetail"));
const ConsignmentDetail = lazy(() => import("@/pages/ConsignmentDetail"));
const OrderReturn = lazy(() => import("@/pages/OrderReturn"));
const AdminRitiro = lazy(() => import("@/pages/AdminRitiro"));
const AdminTessera = lazy(() => import("@/pages/AdminTessera"));
const AdminCliente = lazy(() => import("@/pages/AdminCliente"));
const Ordini = lazy(() => import("@/pages/Ordini"));
const Classifica = lazy(() => import("@/pages/Classifica"));
const Tornei = lazy(() => import("@/pages/Tornei"));
const WantedSeason = lazy(() => import("@/pages/WantedSeason"));
const ChiSiamo = lazy(() => import("@/pages/ChiSiamo"));
const Contatti = lazy(() => import("@/pages/Contatti"));
const NoteLegali = lazy(() => import("@/pages/NoteLegali"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("@/pages/CookiePolicy"));
const Termini = lazy(() => import("@/pages/Termini"));
const Bundle = lazy(() => import("@/pages/Bundle"));
const DeckBuilder = lazy(() => import("@/pages/DeckBuilder"));
const DecklistTrack = lazy(() => import("@/pages/DecklistTrack"));
const Auth = lazy(() => import("@/pages/Auth"));
const AuthCallback = lazy(() => import("@/pages/AuthCallback"));
const Admin = lazy(() => import("@/pages/Admin"));
const Faq = lazy(() => import("@/pages/Faq"));
const DiventaCreator = lazy(() => import("@/pages/DiventaCreator"));
const NewsletterUnsubscribe = lazy(() => import("@/pages/NewsletterUnsubscribe"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Gate di manutenzione: chiede al SERVER lo stato (nessun bypass da URL/localStorage).
// Attiva → solo la pagina di cortesia per i visitatori; /login resta raggiungibile ma SENZA layout del sito
// (niente navbar/footer/tab: solo il form di accesso); l'admin autenticato vede tutto normalmente.
function MaintenanceGate({ children }) {
  const { user } = useAuth(); // undefined = sta caricando, null = ospite
  const [maint, setMaint] = useState(undefined);
  const isAdmin = ["admin","master_admin"].includes(user?.role);

  useEffect(() => {
    let alive = true;
    api.get("/maintenance-status").then((r) => alive && setMaint(!!r.data.maintenance)).catch(() => alive && setMaint(false));
    return () => { alive = false; };
  }, [isAdmin]);

  const loc = useLocation();
  if (maint === undefined || user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <img src="/logo-rosa.png" alt="" className="w-14 h-14 object-contain animate-pulse" />
      </div>
    );
  }
  if (maint && !isAdmin) {
    if (loc.pathname === "/login" || loc.pathname === "/auth/callback") return loc.pathname === "/login" ? <Auth /> : <AuthCallback />;
    return <Maintenance />;
  }
  return children;
}

function App() {
  useEffect(() => {
    // Link creator (?ref=CODICE): registra il click lato server e conserva il token per l'ordine
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref && ref.toUpperCase() !== getCreatorCode()) {
      api.get(`/creator/track/${encodeURIComponent(ref)}`)
        .then((r) => {
          try { localStorage.setItem("creator_ref", JSON.stringify({ token: r.data.token, name: r.data.name })); } catch {}
          toast.success(`Link di ${r.data.name} attivo: i tuoi acquisti sostengono questo creator!`);
        })
        .catch(() => {});
    }
    // Le finestre a scorrimento in sovraimpressione (tendine ricerca, modali, carrello) devono
    // scorrere da sole con la rotella: intercettiamo la wheel in capture PRIMA di Lenis e,
    // se il gesto nasce dentro un contenitore scrollabile, la togliamo a Lenis (scroll nativo).
    const insideScrollable = (node) => {
      let el = node;
      while (el && el !== document.body && el !== document.documentElement) {
        const s = window.getComputedStyle(el);
        if ((s.overflowY === "auto" || s.overflowY === "scroll") && el.scrollHeight > el.clientHeight) return true;
        el = el.parentElement;
      }
      return false;
    };
    const wheelGuard = (e) => {
      if (insideScrollable(e.target)) e.stopPropagation();
    };
    window.addEventListener("wheel", wheelGuard, { capture: true, passive: true });

    const lenis = new Lenis({
      duration: 1.15,
      smoothWheel: true,
      prevent: (node) => insideScrollable(node),
    });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("wheel", wheelGuard, { capture: true });
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <FavoritesProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <MaintenanceGate>
          <div className="min-h-screen text-white font-body">
            <div className="grain-overlay" aria-hidden />
            <Navbar />
            <Suspense fallback={<div data-testid="route-loading"><BussolaLoader /></div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/catalogo" element={<Catalogo />} />
              <Route path="/bundle" element={<Bundle />} />
              <Route path="/deck-builder" element={<DeckBuilder />} />
              <Route path="/decklist/:id/:token" element={<DecklistTrack />} />
              <Route path="/prodotto/:id" element={<ProductDetail />} />
              <Route path="/fedelta" element={<Fedelta />} />
              <Route path="/profilo" element={<Profilo />} />
              <Route path="/arena" element={<Navigate to="/fedelta" replace />} />
              <Route path="/admin/cliente/:id" element={<AdminCliente />} />
              <Route path="/ordini" element={<Ordini />} />
              <Route path="/classifica" element={<Navigate to="/tornei" replace />} />
              <Route path="/tornei" element={<Tornei />} />
              <Route path="/wanted-season" element={<WantedSeason />} />
              <Route path="/chi-siamo" element={<ChiSiamo />} />
              <Route path="/servizi" element={<Servizi />} />
              <Route path="/eventi" element={<Eventi />} />
              <Route path="/vetrina/:id" element={<ConsignmentDetail />} />
              <Route path="/wanted" element={<Wanted />} />
              <Route path="/wanted/:id" element={<WantedDetail />} />
              <Route path="/ordine/esito" element={<OrderReturn />} />
              <Route path="/admin/ritiro/:token" element={<AdminRitiro />} />
              <Route path="/admin/tessera/:token" element={<AdminTessera />} />
              <Route path="/contatti" element={<Contatti />} />
              <Route path="/faq" element={<Faq />} />
              <Route path="/carrello" element={<Navigate to="/" replace />} />
              <Route path="/diventa-creator" element={<DiventaCreator />} />
              <Route path="/newsletter/disiscrivi" element={<NewsletterUnsubscribe />} />
              <Route path="/note-legali" element={<NoteLegali />} />
              <Route path="/termini" element={<Termini />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/cookie" element={<CookiePolicy />} />
              <Route path="/login" element={<Auth />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/admin" element={<Admin />} />
            </Routes>
            </Suspense>
            <Footer />
            <CartDrawer />
            <CookieBanner />
            <Mascotte />
            <NewsletterPopup />
            <CreatorRefSync />
            <ServerDownBanner />
            <Toaster
              theme="dark"
              position="bottom-right"
              toastOptions={{ style: { background: "#141416", border: "1px solid rgba(255,255,255,0.12)", color: "#fff" } }}
            />
          </div>
          </MaintenanceGate>
        </BrowserRouter>
      </CartProvider>
      </FavoritesProvider>
    </AuthProvider>
  );
}

export default App;
